import type {
  DateSelectInfo,
  DateSpanApi,
  DatesSetInfo,
  EventApi,
  EventClickInfo,
  EventDisplayInfo,
  EventResizeDoneInfo,
  MountInfo,
  SlotHeaderInfo,
} from '@fullcalendar/react';
import type { AuthenticatedUser } from '@src/features/users';
import { formatIsoDate, isSameDate, toTime } from '@src/shared/lib';
import { useCallback, useMemo, useRef } from 'react';
import {
  ABS_DAY_VIEW,
  canBackdateAbsence,
  canBookForUser,
  canManageAbsenceUser,
  getAbsenceSelectionById,
  getAbsencesIdsForRange,
  getUserAbsences,
  groupAbsencesByUserDate,
  hasMixedHourlyTypes,
  hydrateSelectedAbsence,
  isAbsenceSelected,
  isBooked,
  isFullyOccupiedSlot,
  namedAbsenceIds,
} from '../lib';
import type { SidebarMode } from '../model/context';
import type {
  Absence,
  AbsenceType,
  ActivityTypeGroup,
  ResourceUser,
  SelectedAbsence,
  TimelineSlot,
} from '../types';
import { getSlotDatefromSelection, getSlotFromEvent } from '.';
import type { CalendarController } from './controller';
import { dateAtClientX, markedDayRun, memberAtDate } from './eventHit';

const DB_CLICK_THRESHOLD = 300;

type TimelineInteractionState = {
  closeBalanceHistory: () => void;
  closeLegend: () => void;
  searchHighlightedUserId: number | null;
  selectedAbsence: SelectedAbsence | null;
  selectedSlot: TimelineSlot | null;
  selectedUserId: number | null;
  setSelectedAbsence: (selection: SelectedAbsence | null) => void;
  setSelectedSlot: (slot: TimelineSlot | null) => void;
  openDraft: (slot: TimelineSlot | null) => void;
  setSelectedUserId: (
    userId: number | null | ((prev: number | null) => number | null),
  ) => void;
  setSidebarMode: (mode: SidebarMode) => void;
  setViewType: (type: string) => void;
  setVisibleRange: (range: { end: Date; start: Date }) => void;
  showBalanceHistory: boolean;
  showLegend: boolean;
  sidebarMode: SidebarMode;
  suppressSelectionUntil: number;
  viewType: string | null;
};

type UseTimelineCalendarHandlersParams = {
  absences: Absence[];
  absenceTypes: AbsenceType[];
  activeSelection: TimelineSlot | null;
  activityTypeGroups: ActivityTypeGroup[];
  calendar: Pick<CalendarController, 'changeView' | 'setDayView' | 'unselect'>;
  currentUser: AuthenticatedUser | null;
  holidayLookup: {
    globalDates: Set<string>;
    isHolidayDate: (date: Date | string, branchId?: number | null) => boolean;
    tooltipsByDate: Map<string, string>;
  };
  hoveredDateKey: string | null;
  /**
   * Whether the rows in hand are this window's own answer.
   *
   * False both before the first answer arrives and while the previous window's
   * answer is being shown in its place. Either way the days drawn are not the
   * days the rows describe.
   */
  absencesLoaded: boolean;
  interactionState: TimelineInteractionState;
  isDayView: boolean;
  isMonthView: boolean;
  isSyncingSelectionRef: React.RefObject<boolean>;
  users: ResourceUser[];
};

type LastDates = null | { end: number; start: number };
type LastClick = { id: number | null; time: number };

function formatTooltipDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}.${month}.${year}`;
}

function getEventAbsenceId(event: EventApi): number {
  return Number(event.extendedProps.absenceId ?? event.id);
}

export default function useTimelineCalendarHandlers({
  absences,
  absencesLoaded,
  absenceTypes,
  activeSelection,
  activityTypeGroups,
  calendar,
  currentUser,
  holidayLookup,
  hoveredDateKey,
  interactionState,
  isDayView,
  isMonthView,
  isSyncingSelectionRef,
  users,
}: UseTimelineCalendarHandlersParams) {
  const lastDatesRef = useRef<LastDates>(null);
  const lastClickRef = useRef<LastClick>({ id: null, time: 0 });
  const userBranchIds = useMemo(
    () => new Map(users.map((user) => [user.id, user.branch?.id ?? null])),
    [users],
  );

  const isHolidayForUserDate = useCallback(
    (date: Date | string, userId?: number | null) => {
      const branchId = userId == null ? null : userBranchIds.get(userId);
      return holidayLookup.isHolidayDate(date, branchId);
    },
    [holidayLookup, userBranchIds],
  );

  const canInteractWithUser = useCallback(
    (targetUserId: number | null | undefined) =>
      canManageAbsenceUser(targetUserId, users, currentUser),
    [currentUser, users],
  );

  const isSameAbsenceSelection = useCallback(
    (currentIds: number[], nextIds: number[]) => {
      const currentId = currentIds[0];
      const nextId = nextIds[0];

      if (!currentId || !nextId) {
        return false;
      }

      const currentSelection = getAbsenceSelectionById(currentId, absences);
      const nextSelection = getAbsenceSelectionById(nextId, absences);

      if (!currentSelection || !nextSelection) {
        return false;
      }

      if (currentSelection.userId !== nextSelection.userId) {
        return false;
      }

      if (
        currentSelection.absenceIds.length !== nextSelection.absenceIds.length
      ) {
        return false;
      }

      return currentSelection.absenceIds.every(
        (absenceId, index) => absenceId === nextSelection.absenceIds[index],
      );
    },
    [absences],
  );

  const shouldIgnoreSelection = useCallback(
    () =>
      isSyncingSelectionRef.current ||
      Date.now() < interactionState.suppressSelectionUntil,
    [interactionState.suppressSelectionUntil, isSyncingSelectionRef],
  );

  const onSelect = useCallback(
    (selectionInfo: DateSelectInfo) => {
      if (shouldIgnoreSelection()) {
        return;
      }

      const selected = getSlotDatefromSelection(selectionInfo, absences);
      // Dragging a new range while the edit pane is open is how a user moves or
      // resizes the absence they opened. The calendar only knows about dates,
      // so the edited absence has to be carried across or the submit reads as
      // a brand new booking -- which is what tells the two apart when only one
      // of dates and type may change at a time.
      const sameUserDraft =
        interactionState.sidebarMode === 'schedule' &&
        interactionState.selectedSlot?.userId === selected.userId;
      const editing =
        sameUserDraft &&
        !!interactionState.selectedSlot?.editedAbsenceIds?.length;
      const slotData = {
        ...selected,
        ...(editing && {
          editedAbsenceId: interactionState.selectedSlot?.editedAbsenceId,
          editedAbsenceIds: interactionState.selectedSlot?.editedAbsenceIds,
          withdrawing: interactionState.selectedSlot?.withdrawing,
        }),
        ...(sameUserDraft && {
          draftTypeId: interactionState.selectedSlot?.draftTypeId,
        }),
      };
      if (!canInteractWithUser(slotData.userId)) {
        calendar.unselect();
        return;
      }
      if (isDayView && slotData.selectedIds.length > 0) {
        calendar.unselect();
        return;
      }
      // Not while the pane is open on an entry: a drag then is that entry
      // being reshaped, and a reshape never covers the same days it did
      // before -- which is what the details branch would ask for before it
      // agreed to stay. Taking it would close the edit and show the days
      // instead, losing what the user was in the middle of.
      const showAbsenceDetails =
        !editing &&
        (isAbsenceSelected(slotData, absences) ||
          (!isDayView && isFullyOccupiedSlot(slotData, absences)));

      if (showAbsenceDetails) {
        const primaryAbsence = absences.find(
          (a) => a.id === slotData.selectedIds[0],
        );
        const selection = {
          userId: slotData.userId,
          start: slotData.start,
          end: slotData.end,
          startTime: primaryAbsence?.startTime ?? slotData.startTime,
          endTime: primaryAbsence?.endTime ?? slotData.endTime,
          absenceIds: slotData.selectedIds,
          primaryAbsenceId: slotData.selectedIds[0],
        };
        const keepEditMode =
          isAbsenceSelected(slotData, absences) &&
          !!interactionState.selectedSlot &&
          interactionState.sidebarMode === 'schedule' &&
          isSameAbsenceSelection(
            interactionState.selectedSlot.selectedIds,
            slotData.selectedIds,
          );

        interactionState.setSelectedAbsence(
          hydrateSelectedAbsence(selection, absences),
        );
        interactionState.setSelectedSlot(keepEditMode ? slotData : null);
        interactionState.setSidebarMode(keepEditMode ? 'schedule' : 'details');
        if (slotData.userId)
          interactionState.setSelectedUserId(slotData.userId);
        return;
      }
      // Selecting free days opens the booking pane, which is open to the
      // person the days belong to and to whoever writes absences for them.
      // Which types they may pick is settled inside the pane.
      if (!canBookForUser(slotData.userId, users, currentUser)) {
        calendar.unselect();
        return;
      }

      interactionState.setSelectedAbsence(null);
      interactionState.setSidebarMode('schedule');
      // A drag that carried the edited absence across is that absence being
      // moved or resized -- the same edit with a different range -- so it
      // adjusts the open draft. Anything else begins one.
      if (editing) {
        interactionState.setSelectedSlot(slotData);
      } else {
        interactionState.openDraft(slotData);
      }
      if (slotData.userId) interactionState.setSelectedUserId(slotData.userId);
    },
    [
      absences,
      calendar,
      canInteractWithUser,
      currentUser,
      interactionState,
      isDayView,
      isSameAbsenceSelection,
      shouldIgnoreSelection,
      users,
    ],
  );

  const onAbsenceClick = useCallback(
    (info: EventClickInfo) => {
      const now = Date.now();
      const root = info.el?.closest<HTMLElement>('.absence-calendar');
      const members = info.event.extendedProps.memberDates as
        | Record<string, number>
        | undefined;
      const date = root ? dateAtClientX(root, info.jsEvent.clientX) : null;
      // Keyboard activation has no pointer position: choose the first member
      // visible in this bar rather than inventing a scope for the whole block.
      const keyboardDate =
        members && info.jsEvent.detail === 0
          ? (Object.keys(members).find((day) => {
              const cell = root?.querySelector<HTMLElement>(
                `[data-absence-date-cell][data-date="${day}"]`,
              );
              if (!cell || !root) return false;
              const rect = cell.getBoundingClientRect();
              const viewport = cell
                .closest<HTMLElement>('.absence-table-header')
                ?.getBoundingClientRect();
              if (!viewport) return false;
              return rect.right > viewport.left && rect.left < viewport.right;
            }) ?? null)
          : null;
      const id = memberAtDate(
        members,
        keyboardDate ?? date,
        getEventAbsenceId(info.event),
      );
      if (id == null) return;
      const { id: prevId, time: prevTime } = lastClickRef.current;
      const isSame = prevId === id;
      const isDbl = isSame && now - prevTime < DB_CLICK_THRESHOLD;

      const absenceId = id;
      const absence = absences.find((item) => item.id === absenceId);
      if (!canInteractWithUser(absence?.userId)) {
        return;
      }

      const isMonthViewPartialAbsence =
        !!absence && isMonthView && !absence.allDay;

      const isMonthViewMultiAbsence =
        !!absence &&
        isMonthView &&
        absences.filter(
          (item) =>
            item.userId === absence.userId && item.dateStr === absence.dateStr,
        ).length > 1;

      if (isMonthViewMultiAbsence) {
        lastClickRef.current = { id, time: now };

        if (interactionState.showLegend) {
          interactionState.closeLegend();
        }
        if (interactionState.showBalanceHistory) {
          interactionState.closeBalanceHistory();
        }

        if (isDbl) {
          const sameDayAbsences = absences
            .filter(
              (item) =>
                item.userId === absence.userId &&
                item.dateStr === absence.dateStr,
            )
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            );

          const firstAbsence = sameDayAbsences[0];
          if (firstAbsence) {
            const selection = getAbsenceSelectionById(
              firstAbsence.id,
              absences,
              true,
            );
            if (selection) {
              interactionState.setSelectedAbsence(selection);
              interactionState.setSidebarMode('details');
            }
          }
          interactionState.setSelectedSlot(null);
          calendar.setDayView(absence.date);
          return;
        }

        const sameDayIds = absences
          .filter(
            (item) =>
              item.userId === absence.userId &&
              item.dateStr === absence.dateStr,
          )
          .map((item) => item.id);

        interactionState.setSidebarMode('details');
        interactionState.setSelectedAbsence(
          hydrateSelectedAbsence(
            {
              userId: absence.userId,
              start: absence.dateStr,
              end: absence.dateStr,
              startTime: null,
              endTime: null,
              absenceIds: sameDayIds,
              primaryAbsenceId: sameDayIds[0],
              isMultiEvent: true,
            },
            absences,
          ),
        );
        interactionState.setSelectedSlot(null);
        interactionState.setSelectedUserId(absence.userId);
        return;
      }

      if (isMonthViewPartialAbsence && isDbl) {
        calendar.setDayView(absence.date);
        lastClickRef.current = { id, time: now };
        return;
      }

      let selection = getAbsenceSelectionById(absenceId, absences, true);
      if (isDbl) {
        selection = getAbsenceSelectionById(absenceId, absences, isDayView);
      }
      if (selection) {
        interactionState.setSidebarMode('details');
        interactionState.setSelectedAbsence(selection);
        interactionState.setSelectedSlot(null);
        if (absence?.userId) interactionState.setSelectedUserId(absence.userId);
      }

      lastClickRef.current = { id, time: now };
    },
    [
      absences,
      calendar,
      canInteractWithUser,
      interactionState,
      isDayView,
      isMonthView,
    ],
  );

  const eventClassNames = useCallback(
    (arg: { event: EventApi }) => {
      const event = arg.event;
      if (event.display === 'background') {
        return event.className;
      }

      const classes = ['abs-base'];
      const day = event.start?.getDay();
      const isDaily =
        !event.extendedProps.memberIds ||
        event.extendedProps.memberIds.length === 1;
      if (isDaily && (day === 0 || day === 6)) {
        classes.push('abs-weekend');
      }
      const eventUserId = event.extendedProps.userId as number | undefined;
      if (
        isDaily &&
        event.start &&
        isHolidayForUserDate(event.start, eventUserId)
      ) {
        classes.push('abs-holiday');
      }
      if (!event.allDay && isMonthView) {
        classes.push('abs-partial');
      }
      if (isDayView) {
        classes.push('abs-day-event');
        if (event.extendedProps.continuesBefore) {
          classes.push('abs-continues-before');
        }
        if (event.extendedProps.continuesAfter) {
          classes.push('abs-continues-after');
        }
      }
      if (event.extendedProps.neutralColour) {
        classes.push('abs-neutral-colour');
      }
      const eventAbsenceId = getEventAbsenceId(event);
      // While withdrawing, the edited ids are the bounds of the withdrawal, not
      // a row being reshaped -- highlight only what the range currently covers.
      const renderedIds: number[] = event.extendedProps.memberIds ?? [
        eventAbsenceId,
      ];
      // A bar drawn from more than one day: hovering and selecting mean a day
      // inside it, not the whole thing.
      if (renderedIds.length > 1) {
        classes.push('abs-aggregated');
      }

      const isMember = (id: number) =>
        activeSelection?.selectedIds.includes(id) ||
        (!activeSelection?.withdrawing &&
          activeSelection?.editedAbsenceIds?.includes(id));

      if (renderedIds.every(isMember)) {
        classes.push('abs-selected');
      } else if (renderedIds.length > 1) {
        // A partial selection inside an aggregated bar. The bar is one element,
        // so `abs-selected` can only say all-or-nothing -- which is why picking
        // one day of a run used to show no feedback at all. Name the run of
        // selected days by index instead and let CSS paint that band.
        const marked = markedDayRun(renderedIds, isMember);
        if (marked) {
          classes.push(
            'abs-day-marked',
            `abs-day-from-${marked.from}`,
            `abs-day-span-${marked.span}`,
            `abs-day-of-${renderedIds.length}`,
          );
        }
      }
      if (
        !canInteractWithUser(event.extendedProps.userId as number | undefined)
      ) {
        classes.push('abs-readonly');
      }
      // Dashed until every approval this absence needs has been given, so a
      // half-approved one still reads as pending. The columns already account
      // for exemptions -- a role that never had to look wrote its approval on
      // creation -- which is why the type's flags do not come into it.
      if (
        !isBooked({
          approvedManager: event.extendedProps.approvedManager,
          approvedHr: event.extendedProps.approvedHr,
        })
      ) {
        classes.push('abs-pending-approval');
      }
      if (event.extendedProps.tentative) {
        classes.push('abs-tentative');
      }
      return classes.join(' ');
    },
    [
      activeSelection,
      canInteractWithUser,
      isDayView,
      isHolidayForUserDate,
      isMonthView,
    ],
  );

  const selectAllow = useCallback(
    (selectionInfo: {
      allDay: boolean;
      end: Date;
      resource?: { extendedProps?: { userId?: number } };
      start: Date;
    }) => {
      const selectedUserId = selectionInfo.resource?.extendedProps?.userId;
      if (!canInteractWithUser(selectedUserId)) {
        return false;
      }
      // Nothing may be selected until this window's absences have arrived: what
      // the selection covers would otherwise be worked out from no rows at all,
      // or from another window's, and a booking over somebody's absence would go
      // out without the warning that says so. An open draft is untouched --
      // this refuses a new selection, it does not close anything.
      if (!absencesLoaded) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectionInfo.start < today) {
        const draft =
          (interactionState.sidebarMode === 'schedule' ||
            interactionState.sidebarMode === 'withdraw') &&
          interactionState.selectedSlot?.userId === selectedUserId
            ? interactionState.selectedSlot
            : null;
        const editedId = draft?.editedAbsenceId ?? draft?.editedAbsenceIds?.[0];
        const editedAbsence = absences.find(
          (absence) => absence.id === editedId,
        );
        if (
          !draft?.withdrawing &&
          draft?.draftTypeId === undefined &&
          editedId != null &&
          editedAbsence?.typeId == null
        )
          return false;
        const typeId =
          draft?.draftTypeId !== undefined
            ? draft.draftTypeId
            : (editedAbsence?.typeId ?? null);
        // Withdrawal narrows an existing absence under the form's own bounds;
        // it does not book past dates.
        if (
          !draft?.withdrawing &&
          !canBackdateAbsence(
            typeId,
            absenceTypes,
            users.find((user) => user.id === selectedUserId),
            currentUser,
          )
        ) {
          return false;
        }
      }

      if (!isDayView || !selectedUserId) {
        return true;
      }

      const overlappingIds = getAbsencesIdsForRange(
        selectionInfo.start,
        selectionInfo.end,
        getUserAbsences(selectedUserId, absences),
        {
          dayView: !selectionInfo.allDay,
          startTime: selectionInfo.allDay ? null : toTime(selectionInfo.start),
          endTime: selectionInfo.allDay ? null : toTime(selectionInfo.end),
        },
      );

      return overlappingIds.length === 0;
    },
    [
      absences,
      absencesLoaded,
      absenceTypes,
      canInteractWithUser,
      currentUser,
      interactionState.selectedSlot,
      interactionState.sidebarMode,
      isDayView,
      users,
    ],
  );

  const eventAllow = useCallback(
    (dropInfo: DateSpanApi, draggedEvent: EventApi | null) => {
      const selectedUserId =
        dropInfo.resource?.extendedProps?.userId ??
        (draggedEvent?.extendedProps.userId as number | undefined);
      if (!canInteractWithUser(selectedUserId)) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dropInfo.start < today) {
        const absence = draggedEvent
          ? absences.find((item) => item.id === getEventAbsenceId(draggedEvent))
          : undefined;
        if (
          absence?.typeId == null ||
          !canBackdateAbsence(
            absence.typeId,
            absenceTypes,
            users.find((user) => user.id === selectedUserId),
            currentUser,
          )
        )
          return false;
      }

      if (!isDayView || !selectedUserId) {
        return true;
      }

      const overlappingIds = getAbsencesIdsForRange(
        dropInfo.start,
        dropInfo.end,
        getUserAbsences(selectedUserId, absences),
        {
          dayView: !dropInfo.allDay,
          startTime: dropInfo.allDay ? null : toTime(dropInfo.start),
          endTime: dropInfo.allDay ? null : toTime(dropInfo.end),
          excludeIds: draggedEvent
            ? [getEventAbsenceId(draggedEvent)]
            : undefined,
        },
      );

      return overlappingIds.length === 0;
    },
    [
      absences,
      absenceTypes,
      canInteractWithUser,
      currentUser,
      isDayView,
      users,
    ],
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetInfo) => {
      const newStart = arg.start.getTime();
      const newEnd = arg.end.getTime();

      const { start: lastStart, end: lastEnd } = lastDatesRef.current || {};
      if (newStart !== lastStart || newEnd !== lastEnd) {
        lastDatesRef.current = { start: newStart, end: newEnd };
        interactionState.setVisibleRange({ start: arg.start, end: arg.end });
      }
      // Only the name crosses over. The view object itself is the renderer's.
      if (interactionState.viewType !== arg.view.type) {
        interactionState.setViewType(arg.view.type);
      }
    },
    [calendar, interactionState],
  );

  /**
   * The day column the pointer is over, shaded by class.
   *
   * It used to be shaded with background *events* -- one per employee row per
   * day -- so moving the pointer one column rebuilt the whole events array,
   * every absence on screen included, on every mouse move. A lane is one cell
   * per column whatever the number of rows, and a class on it costs nothing.
   *
   * The same mechanism the hovered header label has always used, one row down.
   */
  /**
   * Day-column shading, drawn on the slot lane rather than as events.
   *
   * The lane is one full-height column behind every row, so a shaded day is
   * shaded across the whole body -- including the strip above the first lane
   * and the group rows, neither of which is a resource and so neither of which
   * a background event can reach.
   *
   * Only what is true of the *day* belongs here. A branch holiday is true of
   * some employees, so it stays a per-row background event: a column would
   * tell the other branches they are off when they are working.
   */
  const slotLaneClassNames = useCallback(
    (arg: { date?: Date }) => {
      const classes = ['absence-slot-lane'];
      if (!arg.date) {
        return classes.join(' ');
      }
      const dateKey = formatIsoDate(arg.date);

      if (isMonthView) {
        const day = arg.date.getDay();
        const isWeekend = day === 0 || day === 6;
        const isGlobalHoliday = holidayLookup.globalDates.has(dateKey);
        if (isWeekend) {
          classes.push('absence-weekend-column');
        }
        if (isGlobalHoliday) {
          classes.push('absence-holiday-column');
        }
        // Today gives way to weekend and holiday shading, as it did when this
        // was a per-resource event, omitted on days already shaded.
        if (
          !isWeekend &&
          !isGlobalHoliday &&
          isSameDate(arg.date, new Date())
        ) {
          classes.push('absence-today-column');
        }
      }

      // Month view only. The highlight means "this day column", and in day
      // view every slot is an hour of the *same* date -- so the key matches
      // all of them and the whole timeline goes grey.
      if (isMonthView && dateKey === hoveredDateKey) {
        classes.push('absence-hovered-lane');
      }
      return classes.join(' ');
    },
    [holidayLookup.globalDates, hoveredDateKey, isMonthView],
  );

  const slotLabelClassNames = useCallback(
    (arg: SlotHeaderInfo) => {
      const classes: string[] = ['absence-date-header'];
      if (isDayView) classes.push('absence-day-view-header');
      if (isDayView && arg.level === 1) {
        classes.push('absence-day-view-date-slot-label');
      }
      if (isDayView && arg.level === 0) {
        classes.push('absence-day-view-hour-slot-label');
      }
      const day = arg.date?.getDay?.();
      const isWeekend = day === 0 || day === 6;
      const isToday = arg.date ? isSameDate(arg.date, new Date()) : false;
      const isHovered = arg.date
        ? formatIsoDate(arg.date) === hoveredDateKey
        : false;
      // v7 numbers header rows *from the bottom up* -- `rowLevel =
      // cellRows.length - rowIndex - 1` -- so the most granular row is 0 and
      // the month is the highest. v6 counted from the top, so every level check
      // here is the reverse of what it was. Month view has three rows
      // (month/weekday/day), so the month is 2.
      const isMonthViewMonthRow = !isDayView && arg.level === 2;
      const isMonthViewWeekdayRow = !isDayView && arg.level === 1;
      const isMonthViewDayRow = !isDayView && arg.level === 0;
      // Weekend, holiday, today and hover shading belong to the rows that name
      // a single day. The month row spans all of them and would light up whole.
      const shouldApplyMonthViewHeaderStyling = !isDayView && arg.level < 2;

      if (isMonthViewMonthRow) {
        classes.push('absence-month-slot-label');
      }
      if (isMonthViewWeekdayRow) {
        classes.push('absence-weekday-slot-label');
      }
      if (isMonthViewDayRow) {
        classes.push('absence-daynum-slot-label');
      }
      if (isWeekend && shouldApplyMonthViewHeaderStyling) {
        classes.push('absence-weekend-slot-label');
      }
      const dateKey = arg.date ? formatIsoDate(arg.date) : '';
      const isHoliday =
        arg.date &&
        (holidayLookup.globalDates.has(dateKey) ||
          holidayLookup.tooltipsByDate.has(dateKey));
      if (isHoliday && shouldApplyMonthViewHeaderStyling) {
        classes.push('absence-holiday-slot-label');
      }
      if (isToday && shouldApplyMonthViewHeaderStyling) {
        classes.push('absence-today-slot-label');
      }
      if (isHovered && shouldApplyMonthViewHeaderStyling) {
        classes.push('absence-hovered-slot-label');
      }

      return classes.join(' ');
    },
    [
      holidayLookup.globalDates,
      holidayLookup.tooltipsByDate,
      hoveredDateKey,
      isDayView,
    ],
  );

  const onSlotLabelClick = useCallback(
    (date: Date) => {
      calendar.changeView(ABS_DAY_VIEW, date);
    },
    [calendar],
  );

  const slotLabelDidMount = useCallback(
    (mountArg: MountInfo<SlotHeaderInfo>) => {
      // Own the date marker: v7 no longer supplies the old timeline cell DOM.
      if (mountArg.date) {
        mountArg.el.dataset.date = formatIsoDate(mountArg.date);
        // The most granular row, whichever view: level 0 under v7's bottom-up
        // numbering, in both the three-row month header and the two-row day one.
        if (mountArg.level === 0) {
          mountArg.el.dataset.absenceDateCell = 'true';
        }
      }
      if (mountArg.view.type === ABS_DAY_VIEW || !mountArg.date) {
        return;
      }
      // Clicking a weekday or day-number label opens that day. The month row
      // names no single date, so it is not clickable.
      const isClickableMonthLabel = mountArg.level < 2;
      if (!isClickableMonthLabel) {
        return;
      }
      mountArg.el.style.cursor = 'pointer';
      mountArg.el.onclick = () => onSlotLabelClick(mountArg.date as Date);

      const dateKey = formatIsoDate(mountArg.date);
      if (holidayLookup.tooltipsByDate.has(dateKey)) {
        mountArg.el.setAttribute('data-holiday-date', dateKey);
      }
    },
    [onSlotLabelClick, holidayLookup.tooltipsByDate],
  );

  const tooltipMap = useMemo(() => {
    const map = new Map<number, string>();
    const grouped = groupAbsencesByUserDate(absences);

    for (const sameDayUserAbsences of grouped.values()) {
      if (!isDayView && hasMixedHourlyTypes(sameDayUserAbsences)) {
        const text = `Multiple events: ${formatTooltipDate(sameDayUserAbsences[0].dateStr)}`;
        for (const item of sameDayUserAbsences) {
          map.set(item.id, text);
        }
        continue;
      }

      for (const absence of sameDayUserAbsences) {
        if (map.has(absence.id)) continue;

        const selection = getAbsenceSelectionById(absence.id, absences);
        if (!selection) continue;

        const absenceType = absenceTypes.find((t) => t.id === absence.typeId);
        const group = absenceType
          ? activityTypeGroups.find((g) => g.id === absenceType.groupId)
          : undefined;
        const groupName = group?.name ?? absenceType?.name ?? '';
        if (!groupName) continue;

        const start = formatTooltipDate(selection.start);
        const end = formatTooltipDate(selection.end);
        const text =
          start === end
            ? `${groupName}: ${start}`
            : `${groupName}: ${start} - ${end}`;

        for (const id of selection.absenceIds) {
          map.set(id, text);
        }
      }
    }
    return map;
  }, [absences, absenceTypes, activityTypeGroups, isDayView]);

  const eventContent = useCallback(
    (args: EventDisplayInfo) => {
      const { label, memberDates, userId, workdayCount } =
        args.event.extendedProps;
      // Kept in generated content so membership follows event updates, not just
      // the mount hook. Only dates and numeric references, never record notes.
      //
      // The employee travels with the days because the light-day band needs
      // both: which days the bar draws, and whose branch decides whether one of
      // them is a holiday.
      const references = memberDates
        ? ` data-absence-members='${JSON.stringify(memberDates)}'` +
          (typeof userId === 'number' ? ` data-absence-user="${userId}"` : '')
        : '';
      const content = label
        ? `<div class="abs-event-content">${label}</div>`
        : !isDayView && workdayCount != null
          ? `<div class="abs-workday-count">${workdayCount}</div>`
          : !isDayView && !args.event.allDay
            ? '<div class="abs-workday-count">...</div>'
            : '';
      return {
        html: `<div class="absence-bar-content"${references}>${content}</div>`,
      };
    },
    [isDayView],
  );

  const eventDidMount = useCallback(
    (arg: { el: HTMLElement; event: EventApi }) => {
      if (arg.event.display === 'background') return;

      const colourFg = arg.event.extendedProps.colourFg as string | undefined;
      const colourBg = arg.event.extendedProps.colourBg as string | undefined;
      const neutralColour = arg.event.extendedProps.neutralColour as
        | boolean
        | undefined;

      arg.el.style.removeProperty('--abs-colour-bg');
      arg.el.style.removeProperty('--abs-colour-fg');

      if (neutralColour) {
        const absenceId = getEventAbsenceId(arg.event);
        if (tooltipMap.has(absenceId)) {
          arg.el.setAttribute('data-tooltip-id', String(absenceId));
        }
        return;
      }

      if (colourBg) {
        arg.el.style.setProperty('--abs-colour-bg', colourBg);
      }
      if (colourFg) {
        arg.el.style.setProperty('--abs-colour-fg', colourFg);
      }

      const absenceId = getEventAbsenceId(arg.event);
      if (tooltipMap.has(absenceId)) {
        arg.el.setAttribute('data-tooltip-id', String(absenceId));
      }
    },
    [tooltipMap],
  );

  const onEventResize = useCallback(
    (info: EventResizeDoneInfo) => {
      const slot = getSlotFromEvent(info.event);
      if (!slot) {
        info.revert();
        return;
      }
      // Dragging an event's edge is the same gesture as dragging a new range
      // over it, and has to be told apart the same way. Where the pane is
      // already open on the absence being resized, this is that edit being
      // given a different range -- the same question, moved -- so it adjusts
      // the open draft and the reading it took is kept. Opening one here would
      // read the entry again and take whatever the calendar last refetched: a
      // token for a shape the user never saw.
      //
      // The day being dragged is enough to tell: an event belongs to one day,
      // and a draft open on the entry it is part of names it among its days.
      const open = interactionState.selectedSlot;
      const sameEdit =
        interactionState.sidebarMode === 'schedule' &&
        open?.userId === slot.userId &&
        slot.editedAbsenceId != null &&
        namedAbsenceIds(open, { infer: false }).includes(slot.editedAbsenceId);

      interactionState.setSelectedAbsence(null);
      interactionState.setSidebarMode('schedule');
      if (!sameEdit || !open) {
        interactionState.openDraft(slot);
        return;
      }
      // Merged into the draft rather than put in its place. What an event
      // carries is a range and the one day it was dragged; what the draft
      // carries is which days are being edited -- and dropping those would
      // leave the next gesture unable to tell this was an edit at all, so a
      // second resize, or a drag after one, would open a draft and read the
      // entry again.
      interactionState.setSelectedSlot({
        ...open,
        ...slot,
        editedAbsenceId: open.editedAbsenceId,
        editedAbsenceIds: open.editedAbsenceIds,
      });
    },
    [interactionState],
  );

  const resourceRowClassNames = useCallback(
    // `resource` is optional in v7: the same info covers cells with no
    // resource behind them.
    (arg: { resource?: { extendedProps?: { userId?: number } } }) => {
      const userId = arg.resource?.extendedProps?.userId;
      const activeHighlightId =
        interactionState.searchHighlightedUserId ??
        interactionState.selectedUserId;
      if (userId == null || userId !== activeHighlightId) {
        return undefined;
      }
      // Month view fills only the free days, with background events from
      // `useTimelineCalendarData`; a lane fill would cover the day shading.
      return isMonthView
        ? 'absence-resource-selected'
        : 'absence-resource-selected absence-resource-selected-fill';
    },
    [
      interactionState.selectedUserId,
      interactionState.searchHighlightedUserId,
      isMonthView,
    ],
  );

  return {
    canInteractWithUser,
    eventAllow,
    eventClassNames,
    eventContent,
    eventDidMount,
    eventTooltipMap: tooltipMap,
    handleDatesSet,
    holidayTooltipsByDate: holidayLookup.tooltipsByDate,
    onAbsenceClick,
    onEventResize,
    onSelect,
    resourceRowClassNames,
    selectAllow,
    slotLaneClassNames,
    slotLabelClassNames,
    slotLabelDidMount,
  };
}
